import { useState, useEffect } from 'react';
import { supabase, supabaseAdmin } from '../../config/supabase';
import { Search, Loader2, Edit, Trash2, RefreshCw, Tag, Star, Eye } from 'lucide-react';
import { Trader } from '../../services/traderService';
import { toast } from '../../components/ui/use-toast';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import EditTraderForm from '../../components/admin/EditTraderForm';
import { triggerKeywordGeneration } from '../../services/keywordService';
import AdminFeatureManager from '../../components/admin/AdminFeatureManager';
import AdminBusinessPreviewModal from '../../components/admin/AdminBusinessPreviewModal';

interface EditingTrader extends Trader {
  isEditing?: boolean;
}

const Hospitality = () => {
  const { adminUser, loading: authLoading } = useAdminAuth();
  const [hospitalityBusinesses, setHospitalityBusinesses] = useState<EditingTrader[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedBusiness, setSelectedBusiness] = useState<Trader | null>(null);
  const [regeneratingKeywords, setRegeneratingKeywords] = useState<Set<number>>(new Set());
  const [featureManagerBusiness, setFeatureManagerBusiness] = useState<EditingTrader | null>(null);
  const [previewBusiness, setPreviewBusiness] = useState<EditingTrader | null>(null);

  useEffect(() => {
    const loadHospitalityBusinesses = async () => {
      if (authLoading) return;
      
      if (!adminUser) {
        setError('Not authorized');
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        // First, get the hospitality business type ID
        console.log('Fetching hospitality business type...');
        const { data: businessTypes, error: businessTypesError } = await supabaseAdmin
          .from('business_types')
          .select('id, name')
          .eq('name', 'Hospitality');

        console.log('Business types query result:', { businessTypes, businessTypesError });

        if (businessTypesError) {
          console.error('Error loading business types:', businessTypesError);
          throw businessTypesError;
        }

        if (!businessTypes || businessTypes.length === 0) {
          console.log('No hospitality business type found. Available business types:');
          // Let's also fetch all business types to see what's available
          const { data: allTypes } = await supabaseAdmin
            .from('business_types')
            .select('id, name');
          console.log('All business types:', allTypes);
          setHospitalityBusinesses([]);
          return;
        }

        const hospitalityBusinessType = businessTypes[0];
        console.log('Found hospitality business type:', hospitalityBusinessType);

        // Fetch hospitality businesses from dedicated table
        console.log('Fetching hospitality businesses from hospitality table');

        const { data: hospitalityRows, error: hospitalityError } = await supabaseAdmin
          .from('hospitality')
          .select('*')
          .order('store_name', { ascending: true });

        if (hospitalityError) {
          console.error('Error loading hospitality businesses:', hospitalityError);
          throw hospitalityError;
        }

        console.log('Found hospitality rows:', hospitalityRows);

        // Now resolve category for each hospitality row
        const hospitalityData = [];
        if (hospitalityRows && hospitalityRows.length > 0) {
          for (const row of hospitalityRows) {
            let categoryInfo = null;
            if (row.profession_id) {
              const { data: category } = await supabaseAdmin
                .from('hospitality_categories')
                .select('id, name, category')
                .eq('id', row.profession_id)
                .single();
              categoryInfo = category || null;
            }
            
            hospitalityData.push({
              ...row,
              hospitality_categories: categoryInfo,
              business_types: hospitalityBusinessType
            });
          }
        }

        console.log('Final hospitality data with categories:', hospitalityData);
        setHospitalityBusinesses(hospitalityData || []);
      } catch (err) {
        console.error('Error loading hospitality businesses:', err);
        setError(err instanceof Error ? err.message : 'Failed to load hospitality businesses');
        toast({
          title: 'Error',
          description: 'Failed to load hospitality businesses data',
          variant: 'destructive',
        });
        setHospitalityBusinesses([]);
      } finally {
        setLoading(false);
      }
    };

    loadHospitalityBusinesses();
  }, [adminUser, authLoading]);

  const handleSearch = (query: string) => {
    setSearchQuery(query.toLowerCase());
  };

  const handleVerifyBusiness = async (business: Trader) => {
    if (!adminUser) {
      toast({
        title: 'Error',
        description: 'Not authorized to perform this action',
        variant: 'destructive',
      });
      return;
    }

    try {
      const { error: updateError } = await supabase
        .from('hospitality')
        .update({ verified: !business.verified })
        .eq('id', business.id);

      if (updateError) throw updateError;

      setHospitalityBusinesses(prev => 
        prev.map(b => 
          b.id === business.id 
            ? { ...b, verified: !b.verified }
            : b
        )
      );

      toast({
        title: 'Success',
        description: `Business ${business.verified ? 'unverified' : 'verified'} successfully`,
      });
    } catch (error) {
      console.error('Error updating business:', error);
      toast({
        title: 'Error',
        description: 'Failed to update business status',
        variant: 'destructive',
      });
    }
  };

  const handleDeleteBusiness = async (business: Trader) => {
    if (!adminUser) {
      toast({
        title: 'Error',
        description: 'Not authorized to perform this action',
        variant: 'destructive',
      });
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${business.store_name}? This action cannot be undone.`)) {
      return;
    }

    try {
      console.log('Starting deletion process for business:', business.id);
      
      // First check if the business still exists and get their auth user ID
      const { data: existingBusiness, error: checkError } = await supabaseAdmin
        .from('hospitality')
        .select('id, user_id, email')
        .eq('id', business.id)
        .single();

      if (checkError) {
        if (checkError.code === 'PGRST116') {
          toast({
            title: 'Info',
            description: 'Business has already been deleted',
          });
          setHospitalityBusinesses(prev => prev.filter(b => b.id !== business.id));
          return;
        }
        throw checkError;
      }

      if (!existingBusiness) {
        toast({
          title: 'Info',
          description: 'Business not found',
        });
        setHospitalityBusinesses(prev => prev.filter(b => b.id !== business.id));
        return;
      }

      // Delete from hospitality table
      const { error: deleteError } = await supabaseAdmin
        .from('hospitality')
        .delete()
        .eq('id', business.id);

      if (deleteError) {
        console.error('Error deleting from hospitality table:', deleteError);
        throw deleteError;
      }

      // Delete the auth user if we have a user_id
      if (existingBusiness.user_id) {
        try {
          const { error: authDeleteError } = await supabaseAdmin.auth.admin.deleteUser(
            existingBusiness.user_id
          );
          
          if (authDeleteError) {
            console.warn('Warning: Failed to delete auth user:', authDeleteError);
          }
        } catch (authError) {
          console.warn('Warning: Auth user deletion failed:', authError);
        }
      }

      // Update local state
      setHospitalityBusinesses(prev => prev.filter(b => b.id !== business.id));

      toast({
        title: 'Success',
        description: `Business "${existingBusiness.email}" deleted successfully`,
      });
    } catch (error) {
      console.error(`Deletion error for business ${business.id}:`, error);
      
      toast({
        title: 'Deletion Error',
        description: (error as Error).message || 'Failed to delete business',
        variant: 'destructive',
      });
    }
  };

  const handleEdit = (business: Trader) => {
    setSelectedBusiness(business);
  };

  const handleSave = (updatedBusiness: Trader) => {
    setHospitalityBusinesses(prev => 
      prev.map(b => b.id === updatedBusiness.id ? updatedBusiness : b)
    );
    setSelectedBusiness(null);
  };

  const handleClose = () => {
    setSelectedBusiness(null);
  };

  const handleRegenerateKeywords = async (business: Trader) => {
    const businessId = Number(business.id);
    if (isNaN(businessId)) return;

    setRegeneratingKeywords(prev => {
      const next = new Set(prev);
      next.add(businessId);
      return next;
    });

    try {
      const success = await triggerKeywordGeneration(businessId, 'hospitality', {
        store_name: business.store_name,
        description: business.description,
        location: business.location,
      });

      if (success) {
        toast({
          title: 'Success',
          description: `Keyword generation started for ${business.store_name}`,
        });
      } else {
        toast({
          title: 'Error',
          description: 'Failed to trigger keyword generation',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error triggering keyword generation for hospitality business:', error);
      toast({
        title: 'Error',
        description: 'Failed to trigger keyword generation',
        variant: 'destructive',
      });
    } finally {
      setRegeneratingKeywords(prev => {
        const next = new Set(prev);
        next.delete(businessId);
        return next;
      });
    }
  };

  const handleToggleMapVisibility = async (business: EditingTrader) => {
    if (!adminUser) {
      toast({
        title: 'Error',
        description: 'Not authorized to perform this action',
        variant: 'destructive',
      });
      return;
    }

    const current = (business as any).show_map ?? true;
    const next = !current;

    try {
      const { error: updateError } = await supabase
        .from('hospitality')
        .update({ show_map: next })
        .eq('id', business.id);

      if (updateError) throw updateError;

      setHospitalityBusinesses(prev =>
        prev.map(b =>
          b.id === business.id
            ? { ...(b as any), show_map: next }
            : b
        )
      );

      toast({
        title: 'Success',
        description: `Map ${next ? 'enabled' : 'disabled'} for this business`,
      });
    } catch (error) {
      console.error('Error updating hospitality map visibility:', error);
      toast({
        title: 'Error',
        description: 'Failed to update map visibility',
        variant: 'destructive',
      });
    }
  };

  // Filter businesses based on search query
  const filteredBusinesses = hospitalityBusinesses.filter(business => {
    // Check if any keyword matches the search query
    const keywordMatch = (business as any).keywords?.some((keyword: string) => 
      keyword.toLowerCase().includes(searchQuery)
    ) || false;
    
    return (
      business.store_name?.toLowerCase().includes(searchQuery) ||
      business.owner_name?.toLowerCase().includes(searchQuery) ||
      business.location?.toLowerCase().includes(searchQuery) ||
      (business as any).hospitality_categories?.name?.toLowerCase().includes(searchQuery) ||
      keywordMatch
    );
  });

  if (authLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Hospitality Businesses</h1>
        <div className="flex items-center space-x-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search businesses..."
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              onChange={(e) => handleSearch(e.target.value)}
            />
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-100 text-red-700 rounded-lg">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredBusinesses.map((business) => (
            <div
              key={business.id}
              className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200"
            >
              <div className="p-6">
                <div className="flex items-start space-x-4">
                  <img
                    src={business.avatar_url || `https://api.dicebear.com/7.x/initials/svg?seed=${business.store_name}`}
                    alt={business.store_name}
                    className="w-16 h-16 rounded-full object-cover"
                  />
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900">{business.store_name}</h3>
                    <p className="text-sm text-gray-600">{business.owner_name}</p>
                    <p className="text-sm text-gray-500">
                      {(business as any).hospitality_categories?.name || 'No category'}
                      {(business as any).hospitality_categories?.category && 
                        ` (${(business as any).hospitality_categories.category})`
                      }
                    </p>
                    <p className="text-sm text-gray-500">{business.location}</p>
                  </div>
                </div>

                <div className="mt-3 flex items-center justify-between text-xs text-gray-600">
                  <span>Map visibility</span>
                  <button
                    type="button"
                    onClick={() => handleToggleMapVisibility(business)}
                    className={`px-2 py-1 rounded-full border text-xs font-medium ${
                      ((business as any).show_map ?? true)
                        ? 'bg-green-50 text-green-700 border-green-200'
                        : 'bg-gray-50 text-gray-500 border-gray-200'
                    }`}
                  >
                    {((business as any).show_map ?? true) ? 'On' : 'Off'}
                  </button>
                </div>
                
                {(business as any).keywords && (business as any).keywords.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <div className="flex items-start gap-2">
                      <Tag className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                      <div className="flex flex-wrap gap-1">
                        {(business as any).keywords.slice(0, 5).map((keyword: string, idx: number) => (
                          <span
                            key={idx}
                            className="inline-block px-2 py-0.5 text-xs bg-blue-50 text-blue-700 rounded-full"
                          >
                            {keyword}
                          </span>
                        ))}
                        {(business as any).keywords.length > 5 && (
                          <span className="inline-block px-2 py-0.5 text-xs text-gray-500">
                            +{(business as any).keywords.length - 5} more
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                <div className="mt-4 pt-4 border-t border-gray-100">
                  <div className="flex justify-between items-center">
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                      business.verified ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {business.verified ? 'Verified' : 'Pending'}
                    </span>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setPreviewBusiness(business)}
                        className="p-2 rounded-full bg-teal-100 hover:bg-teal-200 text-teal-600 transition-colors duration-200"
                        title="Quick Preview"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setFeatureManagerBusiness(business)}
                        disabled={!business.user_id}
                        className="p-2 rounded-full bg-amber-100 hover:bg-amber-200 text-amber-600 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        title={business.user_id ? 'Manage Features' : 'No user_id - cannot manage features'}
                      >
                        <Star className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleRegenerateKeywords(business)}
                        disabled={regeneratingKeywords.has(Number(business.id))}
                        className="p-2 rounded-full bg-purple-100 hover:bg-purple-200 text-purple-600 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Regenerate Keywords"
                      >
                        <RefreshCw className={`w-4 h-4 ${regeneratingKeywords.has(Number(business.id)) ? 'animate-spin' : ''}`} />
                      </button>
                      <button
                        onClick={() => handleVerifyBusiness(business)}
                        className={`p-2 rounded-full transition-colors duration-200 ${
                          business.verified
                            ? 'bg-yellow-100 hover:bg-yellow-200 text-yellow-600'
                            : 'bg-gray-100 hover:bg-gray-200 text-gray-600'
                        }`}
                        title={business.verified ? 'Unverify Business' : 'Verify Business'}
                      >
                        {business.verified ? '✓' : '○'}
                      </button>
                      <button
                        onClick={() => handleEdit(business)}
                        className="p-2 rounded-full bg-blue-100 hover:bg-blue-200 text-blue-600 transition-colors duration-200"
                        title="Edit Business"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteBusiness(business)}
                        className="p-2 rounded-full bg-red-100 hover:bg-red-200 text-red-600 transition-colors duration-200"
                        title="Delete Business"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      
      {selectedBusiness && (
	        <EditTraderForm
	          trader={selectedBusiness as any}
	          sourceTable="hospitality"
	          onClose={handleClose}
	          onSave={handleSave as any}
	        />
	      )}

      {/* Feature Manager Modal */}
      {featureManagerBusiness && featureManagerBusiness.user_id && (
        <AdminFeatureManager
          userId={featureManagerBusiness.user_id}
          businessName={featureManagerBusiness.store_name}
          onClose={() => setFeatureManagerBusiness(null)}
          onSave={() => {
            toast({
              title: 'Success',
              description: `Features updated for ${featureManagerBusiness.store_name}`,
            });
          }}
        />
      )}

      {previewBusiness && (
        <AdminBusinessPreviewModal
          businessType="hospitality"
          business={previewBusiness}
          contextLabel={(previewBusiness as any).hospitality_categories?.name}
          onClose={() => setPreviewBusiness(null)}
        />
      )}
    </div>
  );
};

export default Hospitality;
